import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Switch } from 'antd';
import Table, { ColumnProps } from 'antd/lib/table';
import { PageTopBar } from '~components/page-top-bar';
import { AnalyticsSearchOptions } from '~components/analytics-search-options';
import { KpiCardShell } from '~analytics-components/kpi-card-shell';
import { KpiTile } from '~analytics-components/kpi-tile';
import { SectionHeader } from '~analytics-components/section-header';
import { OverviewToolbar } from '~containers/analytics/overview-page/overview-toolbar';
import { usePageFilter } from '~containers/analytics/overview-page/use-page-filter';
import { resolveComparePeriod } from '~containers/analytics/kpis/resolvers';
import {
    ApiPeriod,
    lastSelectableDay,
    periodQuery,
} from '~containers/analytics/kpis/periods';
import type { PageCampaign } from '~containers/analytics/overview-page/page-filter';
import {
    useAnalyticsStore,
    useApplicationSelectionStore,
    useAuthStore,
} from '~contexts/mobx';
import { useNavigation } from '~utils/use-navigation';
import { useSearchParams } from '~utils/use-search-params';
import type { KpiEnvelope } from '~stores/kpi-envelope';
import type { TaxiPhoneResponse, TaxiPhoneRow } from '~stores/analytics';
import {
    buildTaxiTileData,
    CAMPAIGN_ID,
    formatTaxiCurrency,
    isTaxiDriverNavVisible,
    TAXI_TILE_SPECS,
} from './cards';
import type { TaxiTileSpec } from './cards';
import style from './style.module.scss';

const DEFAULT_PER_PAGE = 10;

/** Not a campaign the API serves: its period travels as these dates, which
 * an ended campaign keeps for good. */
const TAXI_CAMPAIGN: PageCampaign = {
    window: { startDate: '2026-07-22', endDate: '2026-09-14' },
};

/**
 * Taxi Driver campaign dashboard: a THROWAWAY, self-contained page
 * for a campaign that ends 2026-09-14. It reuses the RPP dashboard's visual layer
 * (KpiCardShell + KpiTile + SectionHeader + toolbar) but consumes the campaign
 * backend contract directly, never touching the standardized KPI source registry.
 *
 * Two fetches per filter change: one campaign-purchases envelope feeding the three
 * real tiles, and one paginated phone-numbers GET feeding the table. No fetch
 * scheduler (only ~2 calls).
 */
export const TaxiDriverPage = observer(() => {
    const analyticsStore = useAnalyticsStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const navigation = useNavigation();
    const search = useSearchParams<{
        period?: string;
        startDate?: string;
        endDate?: string;
        compare?: boolean;
    }>();

    // Page-level access guard, mirroring the sibling analytics pages: redirect
    // anyone without management / customer-service access, and (the campaign is
    // Couche-Tard-only) anyone not in the Couche-Tard app context. Keyed on the
    // selected app too, so switching app while mounted redirects instead of
    // leaving CT campaign data (including customer phone numbers) on screen.
    // Computed at render scope so the fetch effects below can early-return on an
    // unauthorized mount and never request campaign data (incl. PII) before the
    // redirect lands.
    const canView =
        (authStore.hasManagementAccess || authStore.isCustomerServiceAgent) &&
        isTaxiDriverNavVisible(applicationSelectionStore.selectedApplication);

    useEffect(() => {
        if (!canView) {
            navigation.goToHome();
        }
    }, [canView, navigation]);

    const { filter, setPeriod, setPreset, setCompare } = usePageFilter({
        search,
        region: 'none',
        defaultPeriod: 'campaign',
        campaign: TAXI_CAMPAIGN,
    });
    const pageRange = filter.range;
    const compareEnabled = filter.compare;

    // Compare window + label for the tiles, derived from the live period. A custom
    // {start,end} range resolves to the immediately-preceding equal-length window
    // ("from previous period"). Recomputed only when the period/toggle change.
    const compare = useMemo(
        () => (compareEnabled ? resolveComparePeriod(pageRange) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [compareEnabled, pageRange.start, pageRange.end, pageRange.preset]
    );

    // --- KPI tiles: one campaign-purchases envelope fetch ---
    const [env, setEnv] = useState<KpiEnvelope | null>(null);
    const [tilesLoading, setTilesLoading] = useState(true);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        // The signal lets the store drop or abort this request when the range
        // changes again, freeing its concurrency slot; a
        // signal-less caller would pin it until it settled.
        const controller = new AbortController();
        setTilesLoading(true);
        const params: Record<string, string> = {
            campaignId: CAMPAIGN_ID,
            ...periodQuery(pageRange),
        };
        // The API derives the compare window; the FE computes the delta.
        if (compare) {
            params.compare = '1';
        }
        analyticsStore
            .fetchKpiEnvelope(
                'analytics/campaign-purchases',
                params,
                controller.signal
            )
            .then((result) => {
                if (cancelled) return;
                setEnv(result);
            })
            .catch(() => {
                // An abandoned request rejects here, after this effect
                // has re-run (cancelled === true), so it is ignored like any
                // other stale result. A live failure shows the empty tiles.
                if (!cancelled) setEnv(null);
            })
            .finally(() => {
                if (!cancelled) setTilesLoading(false);
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [
        canView,
        analyticsStore,
        pageRange.start,
        pageRange.end,
        pageRange.preset?.period,
        compare,
    ]);

    // --- Phone table: one plain paginated GET, refetched on period/page change ---
    const [phonePage, setPhonePage] = useState(1);
    const [phonePerPage, setPhonePerPage] = useState(DEFAULT_PER_PAGE);
    const [phoneData, setPhoneData] = useState<TaxiPhoneResponse | null>(null);
    const [phoneLoading, setPhoneLoading] = useState(true);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setPhoneLoading(true);
        analyticsStore
            .fetchCampaignPhoneNumbers({
                campaignId: CAMPAIGN_ID,
                startDate: pageRange.start,
                endDate: pageRange.end,
                page: phonePage,
                perPage: phonePerPage,
            })
            .then((result) => {
                if (cancelled) return;
                // Assign even when null: a failed or mis-shaped fetch must clear
                // the table (no page-1 rows left showing as fresh under a new
                // page number), mirroring the tiles effect's fail-safe.
                setPhoneData(result);
            })
            .finally(() => {
                if (!cancelled) setPhoneLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [
        canView,
        analyticsStore,
        pageRange.start,
        pageRange.end,
        phonePage,
        phonePerPage,
    ]);

    // Changing the period resets the table to page 1 (batched with the URL update,
    // so start/end + page settle in a single fetch). Compare does not touch the
    // table (the endpoint takes no compare params).
    const handlePeriodChange = (dates: Parameters<typeof setPeriod>[0]) => {
        setPeriod(dates);
        setPhonePage(1);
    };
    const handlePresetChange = (preset: ApiPeriod) => {
        setPreset(preset);
        setPhonePage(1);
    };

    const renderTile = (spec: TaxiTileSpec) => {
        const data = buildTaxiTileData(env, spec, compare?.label);
        return (
            <KpiCardShell
                key={spec.key}
                title={spec.title}
                size="sm"
                isLoading={tilesLoading}
            >
                {data ? (
                    <KpiTile
                        kpi={{
                            value: data.value,
                            isCurrency: data.isCurrency,
                            trend: data.trend,
                            flat: true,
                        }}
                    />
                ) : (
                    <span
                        className={`${style.section_footnote} footnote_description`}
                    >
                        Data unavailable
                    </span>
                )}
            </KpiCardShell>
        );
    };

    const columns = useMemo<ColumnProps<TaxiPhoneRow>[]>(
        () => [
            {
                title: 'Phone number',
                dataIndex: 'phoneNumber',
                key: 'phoneNumber',
            },
            {
                title: 'Visits',
                dataIndex: 'visits',
                key: 'visits',
                width: '8rem',
                render: (visits: number) => visits.toLocaleString('en-US'),
            },
            {
                title: 'Total spend',
                dataIndex: 'totalSpend',
                key: 'totalSpend',
                width: '10rem',
                render: (totalSpend: number) => formatTaxiCurrency(totalSpend),
            },
            {
                title: 'Avg. spend / transaction',
                dataIndex: 'avgSpend',
                key: 'avgSpend',
                width: '12rem',
                render: (avgSpend: number) => formatTaxiCurrency(avgSpend, 2),
            },
        ],
        []
    );

    const shownCount = phoneData?.results.length ?? 0;
    const total = phoneData?.total ?? 0;

    return (
        <>
            <PageTopBar title="Taxi driver" />

            <div className={`${style.dashboard} content_with_margins`}>
                <OverviewToolbar
                    filters={
                        <AnalyticsSearchOptions
                            startDate={pageRange.start}
                            endDate={pageRange.end}
                            updateParams={handlePeriodChange}
                            lastSelectableDay={lastSelectableDay()}
                            periodPresets={{
                                active: filter.preset,
                                onSelect: handlePresetChange,
                                campaign: TAXI_CAMPAIGN.window,
                            }}
                        />
                    }
                />

                <div className={style.compare_row}>
                    <span
                        className={`${style.compare_label} footnote_description`}
                    >
                        Compare to previous period
                    </span>
                    <Switch checked={compareEnabled} onChange={setCompare} />
                </div>

                <section className={style.section}>
                    <SectionHeader title="LIFT campaign performance" />
                    <div className={`${style.grid} ${style.grid_4}`}>
                        {TAXI_TILE_SPECS.map(renderTile)}
                    </div>
                </section>

                <section className={style.section}>
                    <SectionHeader title="LIFT recorded phone numbers" />
                    <span
                        className={`${style.section_footnote} footnote_description`}
                    >
                        Showing {shownCount} of {total}
                    </span>
                    <Table<TaxiPhoneRow>
                        rowKey="phoneNumber"
                        dataSource={phoneData?.results}
                        columns={columns}
                        loading={phoneLoading}
                        bordered
                        pagination={{
                            current: phonePage,
                            pageSize: phonePerPage,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showQuickJumper: false,
                        }}
                        onChange={(pagination) => {
                            const nextPerPage =
                                pagination.pageSize ?? phonePerPage;
                            if (nextPerPage !== phonePerPage) {
                                setPhonePerPage(nextPerPage);
                                setPhonePage(1);
                            } else {
                                setPhonePage(pagination.current ?? phonePage);
                            }
                        }}
                    />
                </section>
            </div>
        </>
    );
});
